import { createConnectQueryKey } from '@connectrpc/connect-query'
import { getBidLimitMatrix } from '@ebalance/proto/bffuser/v1/bid_limit_service-BidLimitService_connectquery'
import {
  getBiddingBlockApprovalRequest,
  listBiddingBlockApprovalRequests,
} from '@ebalance/proto/bffuser/v1/bidding_block_service-BiddingBlockService_connectquery'
import {
  getBiddingIntradayOrder,
  listBiddingIntradayActiveOrderCounts,
  listBiddingIntradayOrders,
  previewBiddingIntradayOrderFeeAdjustedPrices,
  previewBiddingIntradayOrderFormulaPrice,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import {
  getBiddingSpotApprovalRequest,
  getBiddingSpotScreen,
  listBiddingSpotApprovalRequests,
  listBiddingSpotOperationLogs,
  previewBiddingSpotBidFeeAdjustedPrices,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service-BiddingSpotService_connectquery'
import { getJepxMemberInfo } from '@ebalance/proto/bffuser/v1/jepx_member_info_service-JepxMemberInfoService_connectquery'
import type { QueryClient } from '@tanstack/react-query'

/**
 * bidding 系 React Query キー定義。
 * spot / block / intraday を一元管理する。
 *
 * useQuery (`@connectrpc/connect-query`) が内部で生成する key と一致させるため、
 * `createConnectQueryKey` 経由で構築する (= hand-rolled string array は不一致で
 * invalidateQueries が silently fail する。 既存 `esljQueryKeys` と同パターン)。
 */
export const biddingQueryKeys = {
  spotScreen: createConnectQueryKey({
    schema: getBiddingSpotScreen,
    cardinality: 'finite',
  }),
  spotList: createConnectQueryKey({
    schema: listBiddingSpotApprovalRequests,
    cardinality: 'finite',
  }),
  spotDetail: createConnectQueryKey({
    schema: getBiddingSpotApprovalRequest,
    cardinality: 'finite',
  }),
  /**
   * オペレーション履歴タブ (`useListBiddingSpotOperationLogs`、 AC-BSP-038)。 タブの
   * パネルは非選択でも mount されたままなので、 履歴を作る spot / block の書き込みで
   * invalidate して取り直す。
   */
  spotOperationLogs: createConnectQueryKey({
    schema: listBiddingSpotOperationLogs,
    cardinality: 'finite',
  }),
  blockList: createConnectQueryKey({
    schema: listBiddingBlockApprovalRequests,
    cardinality: 'finite',
  }),
  blockDetail: createConnectQueryKey({
    schema: getBiddingBlockApprovalRequest,
    cardinality: 'finite',
  }),
  intradayList: createConnectQueryKey({
    schema: listBiddingIntradayOrders,
    cardinality: 'finite',
  }),
  intradayDetail: createConnectQueryKey({
    schema: getBiddingIntradayOrder,
    cardinality: 'finite',
  }),
  /** 板発注のコマリボンの自社の入札中の札の件数 (`useActiveOrderCounts`、 AC-BIN-025)。 */
  intradayActiveOrderCounts: createConnectQueryKey({
    schema: listBiddingIntradayActiveOrderCounts,
    cardinality: 'finite',
  }),
  intradayFeeAdjustedPricePreview: createConnectQueryKey({
    schema: previewBiddingIntradayOrderFeeAdjustedPrices,
    cardinality: 'finite',
  }),
  /** フォーミュラ価格のプレビュー (`useIntradayFormulaPricePreview`、 AC-BIN-005c)。 */
  intradayFormulaPricePreview: createConnectQueryKey({
    schema: previewBiddingIntradayOrderFormulaPrice,
    cardinality: 'finite',
  }),
  /** スポットの入札価格のプレビュー (`useSpotFeeAutoConsiderPreview`、 AC-BSP-004b)。 */
  spotFeeAdjustedPricePreview: createConnectQueryKey({
    schema: previewBiddingSpotBidFeeAdjustedPrices,
    cardinality: 'finite',
  }),
  /** JEPX 会員情報 (`useSpotFeeYenPerKwh` が読む。 スポットの手数料単価と会員の登録)。 */
  jepxMemberInfo: createConnectQueryKey({
    schema: getJepxMemberInfo,
    cardinality: 'finite',
  }),
  /** 入札制限の設定 (`useBidLimits` が読む。 手数料自動考慮の ON/OFF を含む)。 */
  bidLimitMatrix: createConnectQueryKey({
    schema: getBidLimitMatrix,
    cardinality: 'finite',
  }),
} as const

// A spot/block write can change both the projection used for polling and the
// full screen snapshot. Keep the legacy detail key here while the additive
// detail RPC/hook remains available, although SpotBidding has no active detail
// query consumer.
export const spotApprovalRequestInvalidationKeys = [
  biddingQueryKeys.spotList,
  biddingQueryKeys.spotDetail,
  biddingQueryKeys.spotScreen,
  biddingQueryKeys.spotOperationLogs,
] as const

export const blockApprovalRequestInvalidationKeys = [
  biddingQueryKeys.blockList,
  biddingQueryKeys.blockDetail,
  biddingQueryKeys.spotScreen,
  biddingQueryKeys.spotOperationLogs,
] as const

export const invalidateSpotApprovalRequestQueries = (
  queryClient: QueryClient,
): Promise<void[]> =>
  Promise.all(
    spotApprovalRequestInvalidationKeys.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  )

export const invalidateBlockApprovalRequestQueries = (
  queryClient: QueryClient,
): Promise<void[]> =>
  Promise.all(
    blockApprovalRequestInvalidationKeys.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  )
